import {useEffect, useState} from 'react';
import {usePermissions} from 'react-admin';
import {
    Avatar,
    Box,
    CircularProgress,
    List,
    ListItemButton,
    TextField,
    Typography,
} from '@mui/material';
import {SearchOutlined} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import StatusPill from '../components/statamic/StatusPill';
import {axiosApiInstance} from '../AxiosUtils';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

// W-02 — moved unchanged from SelectExistingSponsorDialog. NOFAM_ADMIN and UMBRELLA_ADMIN are the
// only roles that ever reach this field (hasPermission gates the entry point on the 'match' action,
// which only those two hold — see auth/hasPermission.js), and V2AdminGuardiansController registers
// the list under both /api/v2/admin and /api/v2/umbrellad.
export const guardianRolePath = (permissions) => (permissions === 'admin' ? 'admin' : 'umbrellad');

// A row's own display name — falls back to email, then the opaque id, so a sponsor with no name on
// file (data import edge case) still renders something selectable.
export const sponsorDisplayName = (sponsor) =>
    [sponsor?.given_name, sponsor?.family_name].filter(Boolean).join(' ') || sponsor?.email || sponsor?.id || '';

// sponsored_child_count_in_scope === 0 marks a sponsor whose sponsorship in this umbrella has ended.
// The field is UMBRELLA_ADMIN-only and absent (undefined) for every other role, and undefined !== 0,
// so an absent field reads as "not a past sponsor" — inverting this would badge every NOFAM_ADMIN
// result as past.
export const isPastSponsor = (sponsor) => sponsor?.sponsored_child_count_in_scope === 0;

// Extracted from the inline call in W-02 so the request contract is testable: W-03 consumes this
// field, and a silent change to either of these would break the picker without failing anything.
// `q` is omitted rather than sent empty — the backend treats an empty q as a search for the empty
// string, not as "no filter".
export const sponsorSearchParams = (query) => ({
    q: query || undefined,
    enabled: true,
    page: 0,
    size: PAGE_SIZE,
});

// A HAL page with no results omits _embedded entirely rather than sending an empty array, so every
// level here has to be optional.
export const sponsorsFromResponse = (data) => data?._embedded?.guardians || [];

/**
 * The sponsor picker as an inline field (D1) rather than the modal it was in W-05.
 *
 * It searches and reports a choice through `onSelect`; it does not attach, notify, or redirect —
 * the add-sponsor page owns what happens next (W-03). The search is a direct debounced GET against
 * the same list endpoint the Sponsors screen uses, because the guardians resource deliberately has
 * no GET_MANY and React Admin cannot resolve a selected id the usual way.
 *
 * D7: this reaches the caller's own umbrella only — current children plus past reach. There is no
 * cross-umbrella lookup, which is why the label asks "already sponsoring with you?" rather than
 * promising the whole platform.
 */
const SponsorSearchField = ({onSelect, translate}) => {
    const {permissions} = usePermissions();
    const rolePath = guardianRolePath(permissions);

    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const handle = setTimeout(() => {
            setLoading(true);
            axiosApiInstance.get(
                `${process.env.REACT_APP_API_URL}/api/v2/${rolePath}/guardians`,
                {params: sponsorSearchParams(query)},
            ).then(({data}) => {
                setResults(sponsorsFromResponse(data));
                setLoading(false);
            }).catch(() => {
                setResults([]);
                setLoading(false);
            });
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [query, rolePath]);

    return (
        <Box>
            <TextField
                fullWidth
                size="small"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={translate('Search sponsors by name, email, city, or country')}
                InputProps={{
                    startAdornment: <SearchOutlined sx={{fontSize: 18, color: t.color.text.muted, mr: 1}}/>,
                }}
            />

            <Box
                sx={{
                    mt: 2,
                    maxHeight: 320,
                    overflowY: 'auto',
                    border: `1px solid ${t.color.border.hairline}`,
                    borderRadius: `${t.radius.md}px`,
                }}
            >
                {loading && (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                        <CircularProgress size={22}/>
                    </Box>
                )}
                {!loading && results.length === 0 && (
                    <Typography
                        sx={{
                            p: 2,
                            fontSize: `${t.type.small.size}px`,
                            color: t.color.text.muted,
                            fontStyle: 'italic',
                        }}
                    >
                        {translate('No sponsors found')}
                    </Typography>
                )}
                {!loading && results.length > 0 && (
                    <List disablePadding>
                        {results.map((sponsor) => (
                            <ListItemButton
                                key={sponsor.id}
                                onClick={() => onSelect(sponsor)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    py: 1,
                                    borderBottom: `1px solid ${t.color.border.hairline}`,
                                    '&:last-of-type': {borderBottom: 'none'},
                                }}
                            >
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: t.color.sidebarHover,
                                        color: t.color.text.secondary,
                                    }}
                                >
                                    {sponsorDisplayName(sponsor).charAt(0).toUpperCase()}
                                </Avatar>
                                <Box sx={{minWidth: 0, flex: 1}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <Typography
                                            sx={{
                                                fontSize: `${t.type.bodyStrong.size}px`,
                                                fontWeight: t.type.bodyStrong.weight,
                                                color: t.color.text.primary,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {sponsorDisplayName(sponsor)}
                                        </Typography>
                                        {isPastSponsor(sponsor) && (
                                            <StatusPill variant="neutral">{translate('Past sponsor')}</StatusPill>
                                        )}
                                    </Box>
                                    <Typography
                                        sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}
                                    >
                                        {sponsor.email}
                                    </Typography>
                                    {isPastSponsor(sponsor) && (
                                        <Typography
                                            sx={{
                                                fontSize: `${t.type.caption.size}px`,
                                                color: t.color.text.muted,
                                                fontStyle: 'italic',
                                            }}
                                        >
                                            {translate('Sponsorship in this umbrella has ended')}
                                        </Typography>
                                    )}
                                </Box>
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    );
};

export default SponsorSearchField;
